import React, { useState } from 'react'
import {
  HomeOutlined as HomeIcon,
  AddOutlined as PlusIcon,
  SettingsOutlined as SettingsIcon,
  ChatOutlined as MessageSquareIcon,
  AccountCircleOutlined as AccountCircleIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useUserStore } from '@client/store'
import { AuthenticationModal } from '@components'
import {
  Tooltip,
  Drawer,
  List,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  IconButton,
  Theme,
  Box,
  MenuItem,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { PyrenzMenu } from '@client/theme'

export function Sidebar({ className }: { className?: string }) {
  const [hovered, setHovered] = useState<string | null>(null)
  const [active, setActive] = useState<string>('Home')
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [modalMode, setModalMode] = useState<'login' | 'register'>('login')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const user_uuid = useUserStore((state) => state.userUUID)

  const menuItemsTop = [
    { name: t('navigation.home'), icon: <HomeIcon fontSize="small" />, path: '/Home', id: 'Home' },
    { name: t('navigation.create'), icon: <PlusIcon fontSize="small" />, path: '/Create', id: 'Create', isCreate: true },
    { name: t('navigation.chats'), icon: <MessageSquareIcon fontSize="small" />, path: '/Archive', id: 'Chats' },
  ]

  const menuItemsBottom = [
    { name: t('navigation.profile'), icon: <AccountCircleIcon fontSize="small" />, path: `/Profile/${user_uuid || ''}`, id: 'Profile' },
    { name: t('navigation.settings'), icon: <SettingsIcon fontSize="small" />, path: '/Settings', id: 'Settings' },
  ]

  const toggleModalMode = () => setModalMode((prev) => (prev === 'login' ? 'register' : 'login'))

  if (isMobile) return null

  return (
    <Box component="aside" className={className} sx={{ width: 72, flexShrink: 0 }}>
      <Drawer
        variant="permanent"
        open
        PaperProps={{
          sx: {
            width: 72,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 3,
            px: 1.5,
            backgroundColor: 'rgba(15, 15, 26, 0.8)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: 'none',
            borderRight: '1px solid rgba(255, 255, 255, 0.06)',
            color: '#fff',
            boxShadow: '4px 0 24px rgba(0, 0, 0, 0.2)',
          },
        }}
      >
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="space-between" 
          height="100%"
          width="100%"
        >
          <List sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 0, gap: 1 }}>
            {menuItemsTop.map((item) => (
              <SidebarItem
                key={item.name}
                item={item}
                hovered={hovered}
                setHovered={setHovered}
                active={active}
                setActive={setActive}
                navigate={navigate}
                setShowLoginModal={setShowLoginModal}
                user_uuid={user_uuid}
                theme={theme}
                setAnchorEl={setAnchorEl}
              />
            ))}
          </List>
          
          <Box sx={{ width: '100%', height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.08)', my: 2 }} />
          
          <List sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 0, gap: 1 }}>
            {menuItemsBottom.map((item) => (
              <SidebarItem
                key={item.name}
                item={item}
                hovered={hovered}
                setHovered={setHovered}
                active={active}
                setActive={setActive}
                navigate={navigate}
                setShowLoginModal={setShowLoginModal}
                user_uuid={user_uuid}
                theme={theme}
                setAnchorEl={setAnchorEl}
              />
            ))}
          </List>
        </Box>
      </Drawer>

      <PyrenzMenu 
        anchorEl={anchorEl} 
        open={Boolean(anchorEl)} 
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(15, 15, 26, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 2,
            mt: 1,
          }
        }}
      >
        <MenuItem
          onClick={() => {
            navigate('/Create?type=character')
            setAnchorEl(null)
            setActive('Create')
          }}
          sx={{
            color: '#fff',
            '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.15)' },
          }}
        >
          Create Character
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate('/Create?type=theme')
            setAnchorEl(null)
            setActive('Create')
          }}
          sx={{
            color: '#fff',
            '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.15)' },
          }}
        >
          Create Theme
        </MenuItem>
      </PyrenzMenu>

      {showLoginModal && (
        <AuthenticationModal mode={modalMode} onClose={() => setShowLoginModal(false)} toggleMode={toggleModalMode} />
      )}
    </Box>
  )
}

interface SidebarItemProps {
  item: { name: string; icon: React.ReactNode; path: string; id: string; isCreate?: boolean }
  hovered: string | null
  setHovered: (name: string | null) => void
  active: string
  setActive: (id: string) => void
  navigate: (path: string) => void
  setShowLoginModal: (show: boolean) => void
  user_uuid: string | null
  theme: Theme
  setAnchorEl: (el: HTMLElement | null) => void
}

function SidebarItem({ item, hovered, setHovered, active, setActive, navigate, setShowLoginModal, user_uuid, theme, setAnchorEl }: SidebarItemProps) {
  const { t } = useTranslation()
  const isActive = active === item.id
  const isHovered = hovered === item.name

  const restricted = [
    t('navigation.settings'),
    t('navigation.create'),
    t('navigation.chats'),
    t('navigation.profile'),
  ]

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (restricted.includes(item.name) && !user_uuid) {
      setShowLoginModal(true)
      return
    }
    if (item.isCreate) {
      setAnchorEl(e.currentTarget)
    } else {
      setActive(item.id)
      navigate(item.path)
    }
  }

  return (
    <Tooltip title={item.name} placement="right" arrow>
      <IconButton
        onClick={handleClick}
        onMouseEnter={() => setHovered(item.name)}
        onMouseLeave={() => setHovered(null)}
        sx={{
          width: 48,
          height: 48,
          color: isActive ? '#667eea' : 'rgba(255, 255, 255, 0.6)',
          backgroundColor: isActive 
            ? 'rgba(102, 126, 234, 0.15)' 
            : isHovered 
              ? 'rgba(255, 255, 255, 0.08)' 
              : 'transparent',
          borderRadius: 3,
          transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': isActive ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 3,
            height: 20,
            backgroundColor: '#667eea',
            borderRadius: '0 4px 4px 0',
          } : {},
          '&:hover': {
            backgroundColor: isActive ? 'rgba(102, 126, 234, 0.2)' : 'rgba(255, 255, 255, 0.1)',
            color: isActive ? '#667eea' : '#fff',
            transform: 'scale(1.05)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
      >
        <ListItemIcon 
          sx={{ 
            color: 'inherit', 
            minWidth: 'auto', 
            justifyContent: 'center',
            transition: 'transform 200ms',
            transform: isHovered ? 'scale(1.1)' : 'scale(1)',
          }}
        >
          {item.icon}
        </ListItemIcon>
      </IconButton>
    </Tooltip>
  )
}